import { useState } from 'react';

import type { Client } from '@frontend/api-types';
import { colors } from '@frontend/constants';
import { useFeatureIsOn, useFeatureValue } from '@frontend/react';
import {
  AppShell,
  Button,
  Divider,
  Icon,
  ScrollView,
  SidebarUserInfo,
  Text,
  Tooltip,
  Wordmark,
  YStack,
  useHotkeys,
  useIsOnMobile,
  useIsOnTablet,
} from '@frontend/web-react';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import { SidebarAnchor, SidebarButton, SidebarNavLink } from '@frontend/web/components/Navigation/SidebarItems';
import {
  useAccessControl,
  useAnalytics,
  useCreateDraftTaskMutation,
  useRetrieveClientQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { spotlight } from '@mantine/spotlight';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';

export type AppSidebarProps = {
  actionButtonDisabled?: boolean;
};

export const AppSidebar = ({ actionButtonDisabled = false }: AppSidebarProps) => {
  const { track } = useAnalytics();
  const isOnMobile = useIsOnMobile();
  const isOnTablet = useIsOnTablet();
  const navigate = useNavigate();
  const { data: client } = useRetrieveClientQuery();
  const { isClientSubscribed } = useAccessControl();
  const matchRoute = useMatchRoute();
  const isTasksActive = !!matchRoute({ fuzzy: true, includeSearch: true, to: '/app/task' });
  // const isNewTaskActive = !!matchRoute({ fuzzy: true, includeSearch: true, to: '/app/draft-task/create' });
  // There must be a less arduous way to do this, but brute forcing for now
  const isHomeActive = !!matchRoute({ fuzzy: true, to: '/app/home' });
  const isDiscoverActive = !!matchRoute({ fuzzy: true, to: '/app/discover' });
  const isSettingsActive = !!matchRoute({ fuzzy: true, to: '/app/settings' });
  const shouldMinimize = Boolean(isSettingsActive || isOnMobile || isOnTablet);
  const { enabled: isDiscoverTabEnabled } = useFeatureIsOn('is_discover_tab_enabled');
  const { value: onboardingHref } = useFeatureValue('onboarding_url');

  const onInitiateTaskCreation = () => {
    track({ data: { entryPoint: 'sidebar_cta' }, event: 'taskCreationStarted', topic: 'draftTasks' });
    navigate({ to: '/app/draft-task/create' });
  };

  const [createDraftTask, { isLoading: isDuckyLoading }] = useCreateDraftTaskMutation();
  const [isDuckyClicked, setIsDuckyClicked] = useState(false);

  const onPressImFeelingDucky = () => {
    setIsDuckyClicked(true);
    track({ event: 'imFeelingDuckySelected', topic: 'draftTasks' });
    track({ data: { entryPoint: 'im_feeling_ducky' }, event: 'taskCreationStarted', topic: 'draftTasks' });
    return handleMutation({
      errorMessage: 'Error creating task',
      mutation: async () => {
        const formData = new FormData();
        formData.append(
          'data',
          JSON.stringify({ description: '', im_feeling_ducky: true, task_source: 'task_source_web_app' }),
        );
        return createDraftTask({ formData } as never).unwrap();
      },
      onError: () => setIsDuckyClicked(false),
      onSuccess: async (draftTask) => {
        track({ data: { entryPoint: 'im_feeling_ducky' }, event: 'draftTaskCreated', topic: 'draftTasks' });
        navigate({
          params: { draftTaskId: draftTask.id },
          search: { source: 'im_feeling_ducky' },
          to: '/app/draft-task/$draftTaskId',
        });
      },
      showSuccessToast: false,
    });
  };

  const handleSearchClick = () => {
    if (isOnMobile) {
      setTimeout(() => spotlight.open(), 50);
    } else {
      spotlight.open();
    }
  };

  useHotkeys([['c', onInitiateTaskCreation]]);

  return (
    <AppShell.Navbar
      classNames={{
        navbar: safeCss('bg-cream-100'),
      }}
      p={shouldMinimize ? 'sm' : 'lg'}
      withBorder={false}
    >
      <AppShell.Section>
        <YStack alignItems={shouldMinimize ? 'center' : undefined} gapY="md">
          {shouldMinimize ? null : (
            <YStack className="py-sm px-md mb-sm cursor-pointer" onClick={() => navigate({ to: '/app/home' })}>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <Wordmark className="h-[20px] w-[107.841px]" fill={colors.textPrimary} />
            </YStack>
          )}
        </YStack>
      </AppShell.Section>
      <AppShell.Section component={ScrollView} grow>
        <YStack className={concatClasses(safeCss('py-md items-center gap-xl'), applyClass(!shouldMinimize, 'px-xs'))}>
          {isClientSubscribed ? (
            shouldMinimize ? (
              <YStack className={safeCss('gap-sm items-center')}>
                <Tooltip classNames={{ tooltip: safeCss('text-textInverse') }} label="New Task" position="right">
                  <button
                    className="flex items-center justify-center rounded-full size-xxl transition-transform active:scale-95 gradient-border-button gradient-border-button-dark"
                    disabled={actionButtonDisabled}
                    onClick={onInitiateTaskCreation}
                    type="button"
                  >
                    <Icon color="white" name="IconPlus" size={24} />
                  </button>
                </Tooltip>
                <Tooltip classNames={{ tooltip: safeCss('text-textInverse') }} label="I'm Feeling Ducky" position="right">
                  <button
                    className="flex items-center justify-center rounded-full size-xxl transition-transform active:scale-95 gradient-border-button"
                    disabled={isDuckyLoading || isDuckyClicked}
                    onClick={onPressImFeelingDucky}
                    type="button"
                  >
                    <span aria-label="duck" role="img">
                      🦆
                    </span>
                  </button>
                </Tooltip>
              </YStack>
            ) : (
              <YStack className={safeCss('gap-sm w-full')}>
                <Button
                  disabled={actionButtonDisabled}
                  fullWidth
                  leftIconColor="white"
                  leftIconName="IconPlus"
                  onClick={onInitiateTaskCreation}
                  size="lg"
                  variant="gradient"
                >
                  Create Task
                </Button>
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="gradient-border-button rounded-[100px]! w-full"
                  disabled={isDuckyClicked}
                  isLoading={isDuckyLoading}
                  onClick={onPressImFeelingDucky}
                  size="lg"
                  variant="tertiary"
                >
                  <span className={safeCss('flex items-center gap-xs')}>
                    <span aria-label="duck" className="mr-1" role="img">
                      🦆
                    </span>
                    {"I'm Feeling Ducky"}
                  </span>
                </Button>
              </YStack>
            )
          ) : null}
          <YStack className={concatClasses(safeCss('gap-xs'), applyClass(shouldMinimize, 'items-center', 'w-full'))}>
            <SidebarNavLink
              isActive={isHomeActive}
              isCollapsed={shouldMinimize}
              label="Home"
              leftIconName={isHomeActive ? 'HomeIconActive' : 'HomeIcon'}
              to="/app/home"
            />
            <SidebarNavLink
              isActive={isTasksActive}
              isCollapsed={shouldMinimize}
              label="Tasks"
              leftIconName={isTasksActive ? 'TaskIconActive' : 'TaskIcon'}
              to="/app/task"
            />
            {isDiscoverTabEnabled ? (
              <SidebarNavLink
                isActive={isDiscoverActive}
                isCollapsed={shouldMinimize}
                label="Discover"
                leftIconName={isDiscoverActive ? 'DiscoverIconActive' : 'DiscoverIcon'}
                to="/app/discover"
              />
            ) : null}
            <SidebarButton
              isCollapsed={shouldMinimize}
              label="Search"
              leftIconName="IconSearch"
              onClick={handleSearchClick}
              rightSection={
                <Text className="tabular-nums" color="tertiary" type="p-sm">
                  ⌘K
                </Text>
              }
            />
          </YStack>
        </YStack>
      </AppShell.Section>
      <Divider mb="xl" />
      <AppShell.Section className="flex flex-col">
        <YStack className={concatClasses(safeCss('gap-xs pb-xs items-center'), applyClass(!shouldMinimize, 'px-xs'))}>
          <SidebarNavLink
            isActive={isSettingsActive}
            isCollapsed={shouldMinimize}
            label="Settings"
            leftIconName="IconSettings"
            to="/app/settings/notifications"
          />
        </YStack>
        {!shouldMinimize && onboardingHref ? (
          <YStack className="gap-md pb-xl px-xs">
            <SidebarAnchor
              href={onboardingHref}
              label="Schedule onboarding"
              leftIconName="IconPhone"
              onClick={() => track({ event: 'scheduleACallSelected', topic: 'onboarding' })}
            />
          </YStack>
        ) : null}
        {client ? (
          <Tooltip
            classNames={{ tooltip: safeCss('text-textInverse') }}
            disabled={!shouldMinimize}
            label="Account"
            position="right"
          >
            <div>
              <SidebarUserInfo
                isCollapsed={shouldMinimize}
                onClick={() => navigate({ to: '/app/profile' })}
                userInfo={
                  {
                    email: client.email,
                    firstName: client.firstName,
                    lastName: client.lastName,
                  } as Client
                }
              />
            </div>
          </Tooltip>
        ) : null}
      </AppShell.Section>
    </AppShell.Navbar>
  );
};
